import { useCallback, useMemo, useState } from 'react'
import useUserStore from '@/user/userStore'
import useAccountStore from '@/account/accountStore'
import { usePrimaryAccount } from '@/account/usePrimaryAccount'
import { useCurrentAccount } from '@/account/useCurrentAccount'
import { useCreateAccount } from '@/account/accountQueries'
import { getAccountConflictId } from '@/account/accountConflictError'
import { AccountTypeFormValues } from './rules/accountTypeSchema'
import { AccountType, Account } from '@/common/types'
import { getDefaultAccountName } from '@/account/getDefaultAccountName'

interface UseAccountTypeLogicResult {
    data: AccountTypeFormValues | undefined
    loading: boolean
    saving: boolean
    isSuccess: boolean
    createdAccount: Account | undefined
    existingAccount: Account | null
    conflictAccountId: string | null
    onSubmit: (data: AccountTypeFormValues) => void
}

/** Find existing account of given type in store */
function findExistingAccount(
    accountType: AccountType,
    primaryAccount: Account | null,
    accounts: Account[],
): Account | null {
    if (accountType === AccountType.INDIVIDUAL) {
        return primaryAccount?.accountType === AccountType.INDIVIDUAL ? primaryAccount : null
    }
    return accounts.find((acc) => acc.accountType === accountType) ?? null
}

export function useAccountTypeLogic(): UseAccountTypeLogicResult {
    const { user, setUser } = useUserStore()
    const activeAccount = useCurrentAccount()
    const primaryAccount = usePrimaryAccount()
    const accounts = useAccountStore((s) => s.getAccounts())
    const createAccount = useCreateAccount()
    const [existingAccount, setExistingAccount] = useState<Account | null>(null)
    const conflictAccountId = useMemo(() => getAccountConflictId(createAccount.error), [createAccount.error])

    const data = useMemo(() => {
        const accountType = activeAccount?.accountType ?? user?.accountType
        if (!accountType) return undefined

        const isTrust = [AccountType.COMPANY_TRUST, AccountType.INDIVIDUAL_TRUST].includes(accountType)

        return {
            accountType,
            isTrust,
        }
    }, [user, activeAccount])

    const onSubmit = useCallback(
        (formData: AccountTypeFormValues) => {
            if (!user) return

            // Check if account of this type already exists
            const existing = findExistingAccount(formData.accountType, primaryAccount, accounts)

            if (existing) {
                // Account exists - reuse it instead of creating new one
                setExistingAccount(existing)
                // TODO: Technical debt - accountType shouldn't be on user (it's on account)
                // Kept for backwards compatibility with KYC/Terms pages that read user?.accountType as fallback
                // Should be refactored to remove user.accountType entirely (~8-10 files affected)
                setUser({ ...user, accountType: formData.accountType })
            } else {
                // Account doesn't exist - create new one
                setExistingAccount(null)
                const accountName = getDefaultAccountName(formData.accountType, user)

                createAccount.mutate({
                    accountType: formData.accountType,
                    accountId: activeAccount?.accountId,
                    accountName,
                })

                // TODO: Technical debt - accountType shouldn't be on user (it's on account)
                // Kept for backwards compatibility with KYC/Terms pages that read user?.accountType as fallback
                // Should be refactored to remove user.accountType entirely (~8-10 files affected)
                setUser({ ...user, accountType: formData.accountType })
            }
        },
        [createAccount, user, setUser, activeAccount, primaryAccount, accounts],
    )

    return {
        data,
        loading: false,
        saving: createAccount.isPending,
        isSuccess: createAccount.isSuccess || existingAccount !== null,
        createdAccount: createAccount.data,
        existingAccount,
        conflictAccountId,
        onSubmit,
    }
}
